// Cliente del gateway LLM (Phase 2) — `/assistant/*`.
//
// Reusa el JWT SHC del usuario. El token MCP PWA lo resuelve el gateway
// en el servidor (no se guarda ni se pega en el navegador).

import { getServerUrl } from '../config';
import { getToken } from './auth';

const baseUrl = (): string => `${getServerUrl().replace(/\/$/, '')}/assistant`;

const authHeaders = (): Record<string, string> => {
  const jwt = getToken();
  const h: Record<string, string> = {};
  if (jwt) h['Authorization'] = `Bearer ${jwt}`;
  return h;
};

const ASSISTANT_RETRY_STATUSES = new Set([502, 503, 504]);

/** @deprecated Usar `assistant.errors.timeout` vía i18n; se mantiene para historial guardado. */
export const ASSISTANT_TIMEOUT_MESSAGE_ES =
  'La consulta tardó demasiado. Probá de nuevo o usá un chip de Ejemplos.';

const ASSISTANT_ERROR_I18N_KEYS: Record<string, string> = {
  pwa_token_not_configured: 'assistant.errors.pwaTokenNotConfigured',
  pwa_token_secret_missing: 'assistant.errors.pwaTokenSecretMissing',
  pwa_token_expired: 'assistant.errors.pwaTokenExpired',
  pwa_token_internal_misconfigured: 'assistant.errors.pwaTokenInternalMisconfigured',
  pwa_token_internal_unreachable: 'assistant.errors.pwaTokenInternalUnreachable',
  pwa_token_internal_error: 'assistant.errors.pwaTokenInternalError',
  ai_not_permitted: 'assistant.errors.aiNotPermitted',
  invalid_pwa_token: 'assistant.errors.invalidPwaToken',
  pwa_token_user_mismatch: 'assistant.errors.pwaTokenUserMismatch',
  invalid_jwt: 'assistant.errors.jwtMissingUserId',
  expired_jwt: 'assistant.snacks.sessionExpired',
  llm_unavailable: 'assistant.errors.llmUnavailable',
};

/** Huellas del gateway en español (cuando solo llega `message`, sin código). */
const GATEWAY_MESSAGE_HINTS: Array<{ re: RegExp; code: string }> = [
  {
    re: /token MCP configurado para la PWA/i,
    code: 'pwa_token_not_configured',
  },
  {
    re: /token PWA es anterior a esta funci[oó]n/i,
    code: 'pwa_token_secret_missing',
  },
  { re: /token PWA est[aá] vencido/i, code: 'pwa_token_expired' },
  {
    re: /no est[aá] configurado para resolver tokens PWA/i,
    code: 'pwa_token_internal_misconfigured',
  },
  {
    re: /contactar al servidor SHC para obtener tu token PWA/i,
    code: 'pwa_token_internal_unreachable',
  },
  {
    re: /Error interno al obtener el token PWA/i,
    code: 'pwa_token_internal_error',
  },
  {
    re: /permiso para usar el Asistente IA/i,
    code: 'ai_not_permitted',
  },
  {
    re: /token PWA configurado ya no es v[aá]lido/i,
    code: 'invalid_pwa_token',
  },
  {
    re: /token PWA no corresponde a tu usuario/i,
    code: 'pwa_token_user_mismatch',
  },
  {
    re: /inteligencia artificial avanzada no est[aá] disponible/i,
    code: 'llm_unavailable',
  },
];

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isAssistantTimeoutStub(
  text: string,
  t?: (key: string) => string
): boolean {
  const trimmed = text.trim().replace(/^_+|_+$/g, '').trim();
  const candidates = [ASSISTANT_TIMEOUT_MESSAGE_ES];
  if (t) {
    candidates.push(t('assistant.errors.timeout'));
  }
  return candidates.some((msg) => {
    const re = new RegExp(
      `^\\s*${escapeRegExp(msg)}\\s*$`,
      'i'
    );
    return re.test(trimmed);
  });
}

export type AssistantParsedError = {
  code?: string;
  message: string;
};

export function parseAssistantErrorDetail(raw: string): AssistantParsedError {
  const htmlTitle = htmlErrorTitle(raw);
  if (htmlTitle) return { message: htmlTitle };
  try {
    const data = JSON.parse(raw);
    const err = data?.error;
    if (err && typeof err === 'object' && err.code === 'llm_unavailable' && err.message) {
      return { code: 'llm_unavailable', message: String(err.message) };
    }
    if (err === 'llm_unavailable' && data?.message) {
      return { code: 'llm_unavailable', message: String(data.message) };
    }
    const detail = data?.detail;
    if (typeof detail === 'object' && detail !== null) {
      const derr = detail.error;
      if (typeof derr === 'object' && derr?.code === 'llm_unavailable' && derr.message) {
        return { code: 'llm_unavailable', message: String(derr.message) };
      }
      if (derr === 'llm_unavailable' && detail.message) {
        return { code: 'llm_unavailable', message: String(detail.message) };
      }
      const code =
        typeof derr === 'string' && derr.trim() ? derr.trim() : undefined;
      if (detail?.message) {
        return { code, message: String(detail.message) };
      }
      if (code) return { code, message: code };
    }
    if (typeof detail === 'string') return { message: detail };
  } catch {
    /* ignore */
  }
  const message = raw.trim();
  for (const hint of GATEWAY_MESSAGE_HINTS) {
    if (hint.re.test(message)) {
      return { code: hint.code, message };
    }
  }
  return { message };
}

export function localizeAssistantError(
  info: AssistantParsedError,
  t: (key: string) => string
): string {
  const code = info.code;
  if (code && ASSISTANT_ERROR_I18N_KEYS[code]) {
    return t(ASSISTANT_ERROR_I18N_KEYS[code]);
  }
  for (const hint of GATEWAY_MESSAGE_HINTS) {
    if (hint.re.test(info.message) && ASSISTANT_ERROR_I18N_KEYS[hint.code]) {
      return t(ASSISTANT_ERROR_I18N_KEYS[hint.code]);
    }
  }
  return info.message;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function htmlErrorTitle(raw: string): string | null {
  const trimmed = raw.trim();
  if (!/^<!DOCTYPE/i.test(trimmed) && !/^<html/i.test(trimmed)) return null;
  const match = trimmed.match(/<title>([^<]+)<\/title>/i);
  return match?.[1]?.trim() || null;
}

/** Mensaje legible desde errores JSON del gateway o HTML de nginx. */
export function parseAssistantErrorText(raw: string): string {
  return parseAssistantErrorDetail(raw).message;
}

function extractHttpErrorBody(raw: string): string {
  const m = raw.match(/ \d{3}: ([\s\S]+)$/);
  return (m?.[1] ?? raw).trim();
}

/** Mensaje corto para la UI (sin HTML crudo), según idioma de la PWA. */
export function formatAssistantClientError(
  err: unknown,
  t: (key: string) => string
): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (/502/.test(raw)) return t('assistant.errors.gateway502');
  if (/503/.test(raw)) return t('assistant.errors.gateway503');
  if (/504/.test(raw)) return t('assistant.errors.gateway504');
  if (/<html/i.test(raw) || /<!DOCTYPE/i.test(raw)) {
    return t('assistant.errors.connection');
  }
  const body = extractHttpErrorBody(raw);
  const localized = localizeAssistantError(parseAssistantErrorDetail(body), t);
  if (localized && localized.length <= 320) return localized;
  if (raw.length > 160) return t('assistant.errors.connection');
  return raw;
}

async function assistantGetJson<T>(
  path: string,
  errorPrefix: string,
  opts?: { retries?: number }
): Promise<T> {
  const url = `${baseUrl()}${path}`;
  const maxAttempts = opts?.retries ?? 8;
  let lastStatus = 0;
  let lastText = '';
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const r = await fetch(url, { method: 'GET', headers: authHeaders() });
    if (r.ok) return r.json() as Promise<T>;
    lastStatus = r.status;
    lastText = await r.text();
    const retryable = ASSISTANT_RETRY_STATUSES.has(r.status);
    if (!retryable || attempt === maxAttempts - 1) {
      throw new Error(
        `${errorPrefix} ${lastStatus}: ${parseAssistantErrorText(lastText)}`
      );
    }
    await sleep(2000 * (attempt + 1));
  }
  throw new Error(
    `${errorPrefix} ${lastStatus}: ${parseAssistantErrorText(lastText)}`
  );
}

// ---------------- Tipos ----------------

export interface SessionStatus {
  llm_model: string;
  warm_models: string[];
  tools_cache: {
    entries: number;
    ttl_s: number;
    items: Array<{ token_fp: string; tools: number; ttl_left_s: number }>;
  };
  warm_age_s: Record<string, number | null>;
  warming: string[];
  warm_debounce_s: number;
  llm_reachable: boolean;
  llm_reachable_error?: string | null;
  included_tools: string[];
  excluded_tools: string[];
}

export interface WarmResult {
  status: 'accepted' | 'debounced' | 'done';
  tools: number;
  requested_models: string[];
  warming?: string[];
  results: Array<{
    model: string;
    ok: boolean;
    elapsed_s: number;
    detail?: string | null;
    tokens_in?: number | null;
  }>;
}

export type ChatRole = 'user' | 'assistant' | 'system' | 'tool';

export interface ChatMessage {
  role: ChatRole;
  content: string | null;
  name?: string;
  tool_call_id?: string;
  tool_calls?: any[];
}

export interface ToolProgressEvent {
  phase: 'start' | 'end';
  name: string;
  id: string;
  isError?: boolean;
}

/** Control diferido al cliente PWA (PIN / cámara) vía SSE `shc.control_deferred`. */
export interface AssistantControlDeferred {
  device_id: number;
  device_name: string;
  pending_action: string;
  pending_action_value?: string | null;
  logical_tool: string;
  control_action?: string;
  user_phrase: string;
  requires_pin: boolean;
  has_cameras: boolean;
  assistant_message?: string;
  idioma?: 'es' | 'en';
}

export interface StreamHandlers {
  onText: (delta: string) => void;
  onTool?: (ev: ToolProgressEvent) => void;
  onControlDeferred?: (payload: AssistantControlDeferred) => void;
  onRoute?: (route: 'fast_path' | 'llm') => void;
  onError?: (err: { message: string; raw?: any }) => void;
  onDone?: () => void;
}

export interface StoredConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  ts: number;
}

export interface ConversationSnapshot {
  messages: StoredConversationMessage[];
  title: string | null;
  updated_at: string | null;
  expires_at: string | null;
}

export async function getSessionStatus(): Promise<SessionStatus> {
  return assistantGetJson<SessionStatus>('/v1/session/status', 'session/status');
}

export async function warmSession(opts?: {
  models?: string[];
  force?: boolean;
  wait?: boolean;
}): Promise<WarmResult> {
  const r = await fetch(`${baseUrl()}/v1/session/warm`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      models: opts?.models,
      force: !!opts?.force,
      wait: !!opts?.wait,
    }),
  });
  if (!r.ok) {
    const txt = await r.text();
    throw new Error(`session/warm ${r.status}: ${parseAssistantErrorText(txt)}`);
  }
  return r.json();
}

function audioFilenameForBlob(blob: Blob): string {
  const t = (blob.type || '').toLowerCase();
  if (t.includes('mp4') || t.includes('aac')) return 'audio.m4a';
  if (t.includes('ogg')) return 'audio.ogg';
  if (t.includes('mpeg') || t.includes('mp3')) return 'audio.mp3';
  if (t.includes('wav')) return 'audio.wav';
  return 'audio.webm';
}

/** Vocabulario del hogar para mejorar nombres de dispositivos en Whisper. */
export const STT_HOME_INITIAL_PROMPT =
  'Comandos del hogar inteligente: encender, apagar, living, dormitorio, baño, rack, baulera, quincho, portón, persianas, pileta, galería, luces, persiana, suite, grupos, estado, listar.';

export async function transcribeAudio(
  blob: Blob,
  opts?: { language?: string; initialPrompt?: string }
): Promise<{ text: string; language?: string }> {
  const fd = new FormData();
  fd.append('audio_file', blob, audioFilenameForBlob(blob));
  if (opts?.language) fd.append('language', opts.language);
  if (opts?.initialPrompt) fd.append('initial_prompt', opts.initialPrompt);
  const r = await fetch(`${baseUrl()}/stt/transcribe`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: fd,
  });
  if (!r.ok) throw new Error(`stt/transcribe ${r.status}: ${await r.text()}`);
  const data = await r.json();
  const text =
    typeof data.text === 'string'
      ? data.text
      : typeof data.transcription === 'string'
      ? data.transcription
      : '';
  return { text, language: data.language };
}

export async function synthesizeSpeech(
  text: string,
  lang: string = 'es'
): Promise<Blob> {
  const r = await fetch(`${baseUrl()}/tts/synthesize`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, lang }),
  });
  if (!r.ok) throw new Error(`tts/synthesize ${r.status}: ${await r.text()}`);
  return r.blob();
}

function streamTrace(...args: unknown[]) {
  // Consola del navegador (Chrome PWA): correlacionar con shc_llm_gateway.log
  console.info('[shc-stream]', ...args);
}

export function streamChat(
  messages: ChatMessage[],
  opts: {
    model?: string;
    temperature?: number;
    max_tokens?: number;
    idioma?: 'es' | 'en';
  },
  handlers: StreamHandlers,
  streamOpts?: { timeoutMs?: number; timeoutMessage?: string }
): AbortController {
  const ctrl = new AbortController();
  // Paridad con SHC_GATEWAY_REQUEST_TIMEOUT (900s): el cliente debe esperar
  // al menos lo que el gateway tolera por llamada a LiteLLM.
  const timeoutMs = streamOpts?.timeoutMs ?? 900000;
  const timeoutMessage =
    streamOpts?.timeoutMessage ?? ASSISTANT_TIMEOUT_MESSAGE_ES;
  const t0 = Date.now();
  let textChars = 0;
  const body = {
    model: opts.model,
    messages,
    stream: true,
    temperature: opts.temperature,
    max_tokens: opts.max_tokens,
    idioma: opts.idioma,
  };

  (async () => {
    let timedOut = false;
    let finished = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      ctrl.abort();
    }, timeoutMs);

    const finish = (err?: { message: string }) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timeoutId);
      streamTrace('finish', {
        ms: Date.now() - t0,
        timedOut,
        textChars,
        error: err?.message,
      });
      if (err) {
        handlers.onError?.(err);
        return;
      }
      handlers.onDone?.();
    };

    let resp: Response;
    try {
      resp = await fetch(`${baseUrl()}/v1/chat/completions`, {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
    } catch (err: any) {
      streamTrace('fetch_error', {
        name: err?.name,
        timedOut,
        ms: Date.now() - t0,
        message: err?.message,
      });
      if (err?.name === 'AbortError' && timedOut) {
        finish({ message: timeoutMessage });
      } else if (err?.name !== 'AbortError') {
        finish({ message: err?.message || String(err) });
      } else {
        finish();
      }
      return;
    }

    streamTrace('fetch_ok', {
      status: resp.status,
      streamId: resp.headers.get('X-Shc-Stream-Id'),
      timeoutMs,
    });

    if (!resp.ok || !resp.body) {
      const txt = await resp.text().catch(() => '');
      finish({
        message: `HTTP ${resp.status}: ${parseAssistantErrorText(txt)}`,
      });
      return;
    }

    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    let pendingEvent: string | null = null;

    const handleData = (rawData: string) => {
      const data = rawData.trim();
      if (!data) return;
      if (data === '[DONE]') return;
      let parsed: any;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      if (pendingEvent === 'shc.tool') {
        handlers.onTool?.(parsed as ToolProgressEvent);
        return;
      }
      if (pendingEvent === 'shc.control_deferred') {
        handlers.onControlDeferred?.(parsed as AssistantControlDeferred);
        return;
      }
      if (pendingEvent === 'shc.route') {
        const route = parsed?.route;
        if (route === 'fast_path' || route === 'llm') {
          streamTrace('route', route);
          handlers.onRoute?.(route);
        }
        return;
      }
      if (parsed.error) {
        finish({
          message: parsed.error?.message || String(parsed.error),
        });
        return;
      }
      const choice = parsed.choices?.[0];
      const delta = choice?.delta?.content;
      if (typeof delta === 'string' && delta.length > 0) {
        textChars += delta.length;
        if (textChars === delta.length) {
          streamTrace('first_text_delta', { len: delta.length, ms: Date.now() - t0 });
        }
        handlers.onText(delta);
      }
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (finished) break;
        buf += dec.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          let evName: string | null = null;
          const dataLines: string[] = [];
          chunk.split('\n').forEach((line) => {
            if (line.startsWith('event:')) {
              evName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim());
            }
          });
          pendingEvent = evName;
          if (dataLines.length) {
            handleData(dataLines.join('\n'));
          }
          if (finished) break;
        }
      }
      finish();
    } catch (err: any) {
      if (err?.name === 'AbortError' && timedOut) {
        finish({ message: timeoutMessage });
      } else if (err?.name !== 'AbortError') {
        finish({ message: err?.message || String(err) });
      } else {
        finish();
      }
    }
  })();

  return ctrl;
}

export interface ControlAuditPayload {
  dispositivo_id: number;
  tool_logica: string;
  accion_pwa: string;
  exito: boolean;
  frase_usuario?: string;
  accion_valor?: string | null;
  mensaje_resultado?: string;
  idioma?: string;
  estado_nuevo?: boolean | string | null;
}

/** Auditoría post-WebSocket de control iniciado por el asistente. */
export async function auditAssistantControl(
  payload: ControlAuditPayload
): Promise<void> {
  const r = await fetch(`${baseUrl()}/v1/control/audit`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `control/audit ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
}

export async function fetchConversation(): Promise<ConversationSnapshot> {
  return assistantGetJson<ConversationSnapshot>(
    '/v1/conversation',
    'conversation GET',
    { retries: 1 },
  );
}

export async function saveConversation(
  messages: StoredConversationMessage[],
  opts?: { keepalive?: boolean }
): Promise<ConversationSnapshot> {
  const r = await fetch(`${baseUrl()}/v1/conversation`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
    keepalive: !!opts?.keepalive,
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `conversation PUT ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
  return r.json();
}

export interface AssistantPreferencesPayload {
  speak_responses: boolean;
  hands_free: boolean;
}

export async function fetchAssistantPreferences(): Promise<AssistantPreferencesPayload> {
  return assistantGetJson<AssistantPreferencesPayload>(
    '/v1/preferences',
    'preferences GET'
  );
}

export async function saveAssistantPreferences(
  prefs: AssistantPreferencesPayload
): Promise<AssistantPreferencesPayload> {
  const r = await fetch(`${baseUrl()}/v1/preferences`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `preferences PUT ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
  return r.json() as Promise<AssistantPreferencesPayload>;
}

export async function deleteConversation(): Promise<void> {
  const r = await fetch(`${baseUrl()}/v1/conversation`, {
    method: 'DELETE',
    headers: { ...authHeaders() },
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `conversation DELETE ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
}

// ---------------- Mensajes favoritos del Asistente ----------------

export interface AssistantMessageFavorite {
  id: number;
  content: string;
  source_msg_ts: number | null;
  created_at: string | null;
}

export interface AssistantMessageFavoritesSnapshot {
  favorites: AssistantMessageFavorite[];
  count: number;
  max: number;
}

export async function fetchMessageFavorites(): Promise<AssistantMessageFavoritesSnapshot> {
  const r = await fetch(`${baseUrl()}/v1/message-favorites`, {
    method: 'GET',
    headers: { ...authHeaders() },
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `message-favorites GET ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
  return r.json();
}

export async function createMessageFavorite(payload: {
  content: string;
  source_msg_ts?: number | null;
}): Promise<{ favorite: AssistantMessageFavorite }> {
  const r = await fetch(`${baseUrl()}/v1/message-favorites`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `message-favorites POST ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
  return r.json();
}

export async function updateMessageFavorite(
  favoriteId: number,
  content: string
): Promise<{ favorite: AssistantMessageFavorite }> {
  const r = await fetch(`${baseUrl()}/v1/message-favorites/${favoriteId}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `message-favorites PUT ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
  return r.json();
}

export async function deleteMessageFavorite(favoriteId: number): Promise<void> {
  const r = await fetch(`${baseUrl()}/v1/message-favorites/${favoriteId}`, {
    method: 'DELETE',
    headers: { ...authHeaders() },
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `message-favorites DELETE ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
}

export async function deleteAllMessageFavorites(): Promise<{ deleted: number }> {
  const r = await fetch(`${baseUrl()}/v1/message-favorites`, {
    method: 'DELETE',
    headers: { ...authHeaders() },
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(
      `message-favorites DELETE all ${r.status}: ${parseAssistantErrorText(txt)}`
    );
  }
  const data = await r.json();
  return { deleted: Number(data?.deleted ?? 0) };
}
